import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import RewardHighlightCard from "../components/RewardHighlightCard";
import { api, parseErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";

const CENTS = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const PS_THRESHOLD = 20;

// CORE tier boundaries — DISPLAY ONLY. Server is authority.
const nextTierAt = (qty) => {
  if (qty < 10) return { qty: 10 - qty, tier_label: "Support (10+)" };
  if (qty < 48) return { qty: 48 - qty, tier_label: "Pallet Best (48+)" };
  return null;
};
const currentTierLabel = (qty) => {
  if (qty <= 9) return { label: "Standard (1–9)", key: "standard" };
  if (qty <= 47) return { label: "Support (10–47)", key: "ten_plus" };
  return { label: "Pallet Best (48+)", key: "forty_eight_plus" };
};
const unitPriceFor = (product, qty) => {
  if (qty <= 9) return product.prices_cents.standard;
  if (qty <= 47) return product.prices_cents.ten_plus;
  return product.prices_cents.forty_eight_plus;
};

export default function CoreProducts() {
  const nav = useNavigate();
  const { cart, setQty, inc } = useCart();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/catalogue");
        if (cancelled) return;
        setAllProducts(r.data.products);
      } catch (err) {
        if (cancelled) return;
        setError(parseErrorDetail(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const products = useMemo(() => allProducts.filter((p) => p.category === "CORE"), [allProducts]);
  const psProducts = useMemo(() => allProducts.filter((p) => p.category === "PREMIUM_SPECIAL"), [allProducts]);
  const psCombined = useMemo(
    () => psProducts.reduce((s, p) => s + (cart[p.product_id] || 0), 0),
    [cart, psProducts]
  );
  const psSupportActive = psCombined >= PS_THRESHOLD;

  const totals = useMemo(() => {
    let subtotal = 0;
    let standardSubtotal = 0;
    products.forEach((p) => {
      const qty = cart[p.product_id] || 0;
      if (qty > 0) {
        subtotal += unitPriceFor(p, qty) * qty;
        standardSubtotal += p.prices_cents.standard * qty;
      }
    });
    return { subtotal_cents: subtotal, savings_cents: Math.max(standardSubtotal - subtotal, 0) };
  }, [cart, products]);

  return (
    <PortalLayout title="Core Products Order">
      <div className="apwo-reward-banner" role="note" data-testid="reward-earn-banner">
        <span className="apwo-reward-banner-badge" aria-hidden>1.5%</span>
        <p className="apwo-reward-banner-text">
          <strong>Prepay orders earn 1.5% Reward</strong>
        </p>
      </div>

      <div className="apwo-sticky-band" data-testid="core-sticky-band">
        <div className={`apwo-ps-mini ${psSupportActive ? "apwo-ps-mini--on" : ""}`} data-testid="core-ps-mini-banner">
          <div className="apwo-ps-mini-body">
            <span className="apwo-ps-mini-count" data-testid="core-ps-count">{psCombined}<span className="apwo-ps-mini-of">/{PS_THRESHOLD} Premium &amp; Special units</span></span>
            <div className="apwo-ps-mini-bar" role="progressbar" aria-valuemin={0} aria-valuemax={PS_THRESHOLD} aria-valuenow={Math.min(psCombined, PS_THRESHOLD)}>
              <div className="apwo-ps-mini-fill" style={{ width: `${Math.min((psCombined / PS_THRESHOLD) * 100, 100)}%` }} />
            </div>
            <span className="apwo-ps-mini-msg">
              {psSupportActive
                ? "Support pricing active on Premium & Special."
                : `${PS_THRESHOLD - psCombined} more Premium & Special unit${PS_THRESHOLD - psCombined === 1 ? "" : "s"} to unlock support pricing.`}
            </span>
          </div>
        </div>
        <RewardHighlightCard />
      </div>

      <div className="apwo-panel apwo-info-panel" data-testid="core-info">
        <span className="apwo-info-icon" aria-hidden>ⓘ</span>
        <div>
          <p className="apwo-info-title">Price gets better as you buy more. Pricing tiers apply per product line.</p>
          <p className="apwo-info-legend">
            <span className="apwo-tier-dot apwo-tier-dot--standard" /> 1–9 = Standard &nbsp;·&nbsp;
            <span className="apwo-tier-dot apwo-tier-dot--support" /> 10–47 = Support &nbsp;·&nbsp;
            <span className="apwo-tier-dot apwo-tier-dot--pallet" /> 48+ = Pallet best
          </p>
        </div>
      </div>

      {loading && <p className="apwo-muted" data-testid="core-loading">Loading catalogue…</p>}
      {error && <div className="apwo-alert-error" data-testid="core-error">{error}</div>}

      {!loading && !error && (
        <div className="apwo-panel apwo-products-panel" data-testid="core-products">
          <table className="apwo-products-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Type</th>
                <th>Specifications</th>
                <th className="apwo-th-qty">Quantity</th>
                <th className="apwo-th-tiers">Pricing tiers (per unit)</th>
                <th className="apwo-th-right">Current price</th>
                <th className="apwo-th-right">Line total (ex GST)</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const qty = cart[p.product_id] || 0;
                const cur = currentTierLabel(qty);
                const unit = unitPriceFor(p, qty);
                const line = unit * qty;
                const next = nextTierAt(qty);
                const outOfStock = p.in_stock === false;
                return (
                  <tr key={p.product_id}
                      className={outOfStock ? "apwo-oos-row" : ""}
                      data-testid={`core-row-${p.product_id}`}>
                    <td>
                      <div className="apwo-sku-badge">
                        {p.display_name}
                        {outOfStock && (
                          <span className="apwo-oos-badge"
                                data-testid={`core-oos-${p.product_id}`}>Out of stock</span>
                        )}
                      </div>
                    </td>
                    <td className="apwo-cell-muted">{p.type}</td>
                    <td className="apwo-cell-muted">{p.specifications}</td>
                    <td>
                      <div className="apwo-qty-wrap">
                        <input
                          type="number" min="0" max="10000" step="1" inputMode="numeric"
                          value={outOfStock ? 0 : qty}
                          onChange={(e) => setQty(p.product_id, e.target.value)}
                          className="apwo-qty-input"
                          aria-label={`Quantity for ${p.display_name}`}
                          disabled={outOfStock}
                          data-testid={`core-qty-${p.product_id}`}
                        />
                        <span className="apwo-qty-units">units</span>
                        <div className="apwo-quick-buttons">
                          {[10, 20, 48, 96].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setQty(p.product_id, n)}
                              disabled={outOfStock}
                              className={`apwo-quick-btn ${qty === n ? "apwo-quick-btn--on" : ""}`}
                              data-testid={`core-quick-${p.product_id}-${n}`}
                            >{n}</button>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td>
                      <ul className="apwo-tier-list">
                        <li className={cur.key === "standard" ? "apwo-tier--on" : ""}>1–9 · Standard · {CENTS(p.prices_cents.standard)}</li>
                        <li className={cur.key === "ten_plus" ? "apwo-tier--on" : ""}>10–47 · Support · {CENTS(p.prices_cents.ten_plus)}</li>
                        <li className={cur.key === "forty_eight_plus" ? "apwo-tier--on" : ""}>48+ · Pallet Best · {CENTS(p.prices_cents.forty_eight_plus)}</li>
                      </ul>
                    </td>
                    <td className="apwo-th-right apwo-price-current">
                      <div className={`apwo-price-cell apwo-price-cell--${cur.key}`}>{CENTS(unit)}</div>
                      <div className="apwo-cell-muted apwo-nowrap">{cur.label}</div>
                    </td>
                    <td className="apwo-th-right apwo-line-total">
                      {CENTS(line)}
                      {next && (
                        <div className="apwo-next-tier" data-testid={`core-next-${p.product_id}`}>
                          Add {next.qty} more to unlock {next.tier_label}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="apwo-panel-footer">
            <div className="apwo-savings-note" data-testid="core-savings">
              <span className="apwo-savings-title">Total savings vs Standard pricing</span>
              <span className="apwo-savings-value">{CENTS(totals.savings_cents)}</span>
            </div>
            <div className="apwo-subtotal">
              <span className="apwo-sub-label">CORE PRODUCTS SUBTOTAL (ex GST)</span>
              <span className="apwo-sub-value" data-testid="core-subtotal">{CENTS(totals.subtotal_cents)}</span>
            </div>
            <button
              type="button"
              className="apwo-btn-primary apwo-btn-primary--nav"
              onClick={() => nav("/portal/premium")}
              data-testid="core-continue"
            >Continue to Premium &amp; Special</button>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
